import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

/** Cor para % de utilizacao */
const getUtilizacaoColor = (pct: number): string => {
  if (pct >= 90) return '#27AE60';
  if (pct >= 70) return '#F39C12';
  return '#E74C3C';
};

/** Transparencia simples via rgba */
const alphaColor = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const MonitoramentoMac: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getMac(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de gestao MAC.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  // --- Dados derivados ---
  const kpis = data?.kpis || {};
  const metasVsExecutadoRaw: any[] = useMemo(() => data?.metasVsExecutado || [], [data]);
  const evolucaoRaw: any[] = data?.evolucao || [];

  // KPIs convertidos
  const tetoMac = Number(kpis.teto_mac ?? 0);
  const valorExecutado = Number(kpis.valor_executado ?? 0);
  const pctUtilizacao = Number(kpis.pct_utilizacao ?? 0);

  // BarChart Meta vs Executado
  const metaVsExecutadoChart = useMemo(
    () =>
      metasVsExecutadoRaw.map((item: any) => ({
        nome_procedimento: item.nome_procedimento,
        valor_pactuado: Number(item.valor_pactuado ?? 0),
        valor_produzido: Number(item.valor_produzido ?? 0),
        quantidade_pactuada: Number(item.quantidade_pactuada ?? 0),
        quantidade_produzida: Number(item.quantidade_produzida ?? 0),
        pct_execucao: Number(item.pct_execucao ?? 0),
        codigo_procedimento: item.codigo_procedimento,
      })),
    [metasVsExecutadoRaw],
  );

  // Evolucao: pivotar flat {anomescompetencia, codigo_procedimento, nome_procedimento, valor, qtd}
  // -> agrupar por anomescompetencia, somar valor como "Executado", usar teto_mac / n_meses como "Teto"
  const evolucaoChart = useMemo(() => {
    // Agrupar por anomescompetencia
    const map = new Map<string, { executado: number; qtd: number }>();
    for (const item of evolucaoRaw) {
      const mes = String(item.anomescompetencia ?? item.mes ?? '');
      if (!map.has(mes)) {
        map.set(mes, { executado: 0, qtd: 0 });
      }
      const row = map.get(mes)!;
      row.executado += Number(item.valor ?? 0);
      row.qtd += Number(item.qtd ?? 0);
    }
    const meses = Array.from(map.keys()).sort();
    const tetoMensal = meses.length > 0 ? tetoMac / meses.length : 0;
    return meses.map((mes) => ({
      competencia: mes,
      Teto: Math.round(tetoMensal * 100) / 100,
      Executado: Math.round((map.get(mes)?.executado ?? 0) * 100) / 100,
    }));
  }, [evolucaoRaw, tetoMac]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de gestao MAC.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Gestao MAC
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Teto MAC"
            valor={`R$ ${tetoMac.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            icone={<AccountBalanceIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Executado"
            valor={`R$ ${valorExecutado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            icone={<TrendingUpIcon />}
            cor="#2D7D9A"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="% Utilizacao"
            valor={`${pctUtilizacao.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
            icone={<PieChartIcon />}
            cor={getUtilizacaoColor(pctUtilizacao)}
          />
        </Grid>
      </Grid>

      {/* Meta vs Executado por procedimento - BarChart agrupado */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Meta vs Executado por Procedimento
        </Typography>
        {metaVsExecutadoChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={metaVsExecutadoChart} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="nome_procedimento"
                tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }}
                height={80}
              />
              <YAxis tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  name === 'valor_pactuado' ? 'Meta (Pactuado)' : 'Executado (Produzido)',
                ]}
              />
              <Legend />
              <Bar dataKey="valor_pactuado" name="Meta (Pactuado)" fill="#1E3A5F" radius={[4, 4, 0, 0]} />
              <Bar dataKey="valor_produzido" name="Executado (Produzido)" fill="#27AE60" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de meta vs executado para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Evolucao financeira mensal - LineChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Evolucao Financeira Mensal
        </Typography>
        {evolucaoChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={evolucaoChart} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="competencia" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  name,
                ]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Teto"
                name="Teto"
                stroke="#1E3A5F"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="Executado"
                name="Executado"
                stroke="#27AE60"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de evolucao financeira para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Tabela detalhada por procedimento */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Detalhamento por Procedimento
        </Typography>
        {metaVsExecutadoChart.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd Pactuada</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd Produzida</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Pactuado (R$)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Produzido (R$)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>% Execucao</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo (R$)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metaVsExecutadoChart.map((row: any, index: number) => {
                  const pct = row.pct_execucao;
                  const saldo = row.valor_pactuado - row.valor_produzido;
                  const corPct = getUtilizacaoColor(pct);
                  return (
                    <TableRow key={index}>
                      <TableCell>{row.nome_procedimento}</TableCell>
                      <TableCell align="right">
                        {row.quantidade_pactuada.toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="right">
                        {row.quantidade_produzida.toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="right">
                        {row.valor_pactuado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="right">
                        {row.valor_produzido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${pct.toFixed(1)}%`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            minWidth: 70,
                            backgroundColor: alphaColor(corPct, 0.15),
                            color: corPct,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: saldo >= 0 ? '#27AE60' : '#E74C3C',
                          fontWeight: 600,
                        }}
                      >
                        {saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados detalhados para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="mac"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoMac;
